"use client";

import { useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles,
  Copy,
  Loader2,
  Wand2,
  ImageDown,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { FavoriteButton } from "@/components/FavoriteButton";
import { QuotaGateCard } from "@/components/QuotaGateCard";
import { buildShareCardUrl, shareOrDownloadCard } from "@/lib/share-card";
import { withClientId } from "@/lib/client-id";
import {
  extractQuota,
  extractQuotaLimits,
  type QuotaInfo,
  type QuotaLimits,
} from "@/lib/quota-client";
import { trackEvent } from "@/lib/track";
import { cn } from "@/lib/utils";
import {
  TIME_HORIZONS,
  FORTUNE_TYPES,
  ORACLE_QUANTITIES,
  MIN_INTENSITY,
  MAX_INTENSITY,
  type TimeHorizon,
  type FortuneType,
} from "@/lib/prompts/oracle";

interface GeneratedFortune {
  message: string;
  luckyNumbers: number[];
  /** 服务端为这条正文签发的 HMAC,分享卡片必须带上它,见 lib/og-signature.ts。 */
  shareSig?: string;
}

const INTENSITY_LABELS: Record<number, string> = {
  1: "Vague",
  2: "Loose",
  3: "Balanced",
  4: "Specific",
  5: "Precise",
};

function SegmentedGroup<T extends string | number>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={value === opt.value}
            className={cn(
              "rounded-xl border px-3 py-2 text-sm font-medium transition-all",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
              value === opt.value
                ? "border-amber-400 bg-amber-100 text-amber-800 dark:border-amber-500/60 dark:bg-amber-500/20 dark:text-amber-200"
                : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export function OracleClient() {
  const [timeHorizon, setTimeHorizon] = useState<TimeHorizon>("today");
  const [intensity, setIntensity] = useState(3);
  const [fortuneTypes, setFortuneTypes] = useState<FortuneType[]>(["good"]);
  const [quantity, setQuantity] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<GeneratedFortune[]>([]);
  const [quotaGate, setQuotaGate] = useState<QuotaInfo | null>(null);
  // 限额数字随 429 一起来自服务端,不再读 NEXT_PUBLIC_ 镜像。
  const [quotaLimits, setQuotaLimits] = useState<QuotaLimits | undefined>(
    undefined,
  );
  const [quotaMessage, setQuotaMessage] = useState<string | undefined>();

  const toggleType = useCallback((type: FortuneType) => {
    setFortuneTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }, []);

  const generate = useCallback(async () => {
    if (fortuneTypes.length === 0) {
      toast.error("Pick at least one fortune type.");
      return;
    }
    setIsLoading(true);
    // Clear any previous gate up front: a stale card (e.g. pre-sign-in guest
    // numbers) left on screen next to a fresh non-429 error reads as "signing
    // in did nothing".
    setQuotaGate(null);
    setQuotaMessage(undefined);
    try {
      const res = await fetch("/api/generator", {
        method: "POST",
        headers: withClientId({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          mode: "oracle",
          source: "generator",
          params: { timeHorizon, intensity, fortuneTypes, quantity },
        }),
      });
      // Bare .json() throws on the HTML error pages a CDN/edge can return,
      // which the outer catch would mislabel as "Network error".
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        // Daily limit: show an in-place card (results above are kept) instead
        // of a toast the user can miss.
        const quota = res.status === 429 ? extractQuota(json) : null;
        if (quota) {
          setQuotaGate(quota);
          setQuotaLimits(extractQuotaLimits(json));
          setQuotaMessage(
            typeof json?.error === "string" ? json.error : undefined,
          );
          return;
        }
        toast.error(json?.error || "Generation failed. Please try again.");
        return;
      }
      const fortunes: GeneratedFortune[] = json?.data?.fortunes ?? [];
      if (fortunes.length === 0) {
        toast.error("The oracle stayed silent. Try again.");
        return;
      }
      // Reported on a successful reveal only. Generator modes have no local
      // fallback (failures surface as a toast and return early), so
      // is_fallback is structurally false here.
      trackEvent("cookie_cracked", {
        source: "generator",
        is_fallback: false,
      });
      setResults(fortunes);
      setQuotaGate(null);
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [timeHorizon, intensity, fortuneTypes, quantity]);

  const copyFortune = useCallback((text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success("Copied"),
      () => toast.error("Copy failed"),
    );
  }, []);

  const shareImage = useCallback(async (fortune: GeneratedFortune) => {
    const url = buildShareCardUrl({
      message: fortune.message,
      luckyNumbers: fortune.luckyNumbers,
      mode: "oracle",
      signature: fortune.shareSig,
    });
    const result = await shareOrDownloadCard(url, fortune.message);
    if (result === "downloaded") toast.success("Image saved");
  }, []);

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,360px)_minmax(0,1fr)]">
      {/* Parameter panel */}
      <div className="space-y-5 rounded-2xl border border-slate-200 bg-white/70 p-6 backdrop-blur dark:border-slate-700 dark:bg-slate-900/60">
        {/* 按钮在最上面,四组控件收进下面的 <details>。
            这一页是 hub 上「Quick Fortune — One tap, one prophecy. No settings
            needed.」指向的落地页,而在此之前它首屏摆的是四组控件加一句「Set your
            parameters」—— 从 SERP 搜 "random fortune cookie generator" 进来的人
            连续两次落空,拿到的点击会直接在这张表单上漏回搜索结果页。
            所有参数本来就有默认值(today / 3 / good / 1),点一下就能出结果,
            微调是可选项,所以它应该长得像可选项。 */}
        <Button
          onClick={generate}
          disabled={isLoading}
          className="h-12 w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-base font-semibold text-white hover:from-amber-600 hover:to-orange-600"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Consulting the oracle…
            </>
          ) : (
            <>
              <Wand2 className="mr-2 h-4 w-4" />
              Reveal {quantity === 1 ? "Fortune" : `${quantity} Fortunes`}
            </>
          )}
        </Button>

        <details className="group">
          <summary className="flex cursor-pointer list-none items-center justify-between rounded-lg px-1 py-2 text-sm font-medium text-slate-600 transition-colors hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-slate-400 dark:hover:text-amber-300">
            <span>Tune it — timeframe, intensity, flavor</span>
            <ChevronDown
              className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>

          <div className="space-y-6 pt-4">
            <SegmentedGroup
              label="Time Horizon"
              options={TIME_HORIZONS}
              value={timeHorizon}
              onChange={setTimeHorizon}
            />

            <div>
              <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                Intensity — {INTENSITY_LABELS[intensity]}
              </p>
              <div className="flex gap-2">
                {Array.from(
                  { length: MAX_INTENSITY - MIN_INTENSITY + 1 },
                  (_, i) => MIN_INTENSITY + i,
                ).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setIntensity(n)}
                    aria-label={`Intensity ${n}`}
                    aria-pressed={intensity === n}
                    className={cn(
                      "h-10 flex-1 rounded-xl border text-sm font-semibold transition-all",
                      n <= intensity
                        ? "border-amber-400 bg-amber-100 text-amber-800 dark:border-amber-500/60 dark:bg-amber-500/20 dark:text-amber-200"
                        : "border-slate-200 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-800",
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                Fortune Type{" "}
                <span className="text-xs font-normal text-slate-400">
                  (choose one or more)
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                {FORTUNE_TYPES.map((opt) => {
                  const active = fortuneTypes.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleType(opt.value)}
                      aria-pressed={active}
                      className={cn(
                        "rounded-xl border px-3 py-2 text-sm font-medium transition-all",
                        active
                          ? "border-amber-400 bg-amber-100 text-amber-800 dark:border-amber-500/60 dark:bg-amber-500/20 dark:text-amber-200"
                          : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300",
                      )}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <SegmentedGroup
              label="Quantity"
              options={ORACLE_QUANTITIES.map((q) => ({
                value: q,
                label: String(q),
              }))}
              value={quantity}
              onChange={setQuantity}
            />
          </div>
        </details>
      </div>

      {/* Results */}
      <div className="min-h-[200px]">
        {quotaGate && (
          <QuotaGateCard
            quota={quotaGate}
            limits={quotaLimits}
            message={quotaMessage}
            className="mb-4"
          />
        )}

        {results.length === 0 && !isLoading && !quotaGate && (
          <div className="flex h-full min-h-[200px] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-400 dark:border-slate-700">
            <Sparkles className="mb-3 h-8 w-8" />
            {/* 不要再写「Set your parameters」—— 参数是可选的,这句话会让人
                以为必须先填表。 */}
            <p>Your fortune appears here. Hit reveal whenever you are ready.</p>
          </div>
        )}

        <ul className="space-y-3 list-none p-0 m-0">
          <AnimatePresence>
            {results.map((fortune, index) => (
              <motion.li
                key={`${index}-${fortune.message.slice(0, 16)}`}
                initial={{ opacity: 0, y: -12, scaleY: 0.6 }}
                animate={{ opacity: 1, y: 0, scaleY: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  delay: index * 0.08,
                  duration: 0.35,
                  ease: "easeOut",
                }}
                style={{ transformOrigin: "top" }}
                className="group relative overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-4 pr-28 shadow-sm dark:border-amber-500/30 dark:from-slate-800 dark:to-slate-800/60"
              >
                <p className="font-serif text-slate-800 dark:text-slate-100">
                  {fortune.message}
                </p>
                {fortune.luckyNumbers?.length > 0 && (
                  <p className="mt-2 text-xs tracking-wider text-amber-600 dark:text-amber-300">
                    Lucky: {fortune.luckyNumbers.join(" · ")}
                  </p>
                )}
                <div className="absolute right-2 top-2 flex items-center">
                  <FavoriteButton
                    variant="icon"
                    size="sm"
                    message={fortune.message}
                    luckyNumbers={fortune.luckyNumbers}
                    theme="oracle"
                  />
                  <button
                    type="button"
                    onClick={() => copyFortune(fortune.message)}
                    aria-label="Copy fortune"
                    className="rounded-lg p-2 text-slate-400 opacity-0 transition-opacity hover:bg-white/60 hover:text-amber-600 group-hover:opacity-100 dark:hover:bg-slate-700/60"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => shareImage(fortune)}
                    aria-label="Share as image"
                    className="rounded-lg p-2 text-slate-400 opacity-0 transition-opacity hover:bg-white/60 hover:text-amber-600 group-hover:opacity-100 dark:hover:bg-slate-700/60"
                  >
                    <ImageDown className="h-4 w-4" />
                  </button>
                </div>
              </motion.li>
            ))}
          </AnimatePresence>
        </ul>
      </div>
    </div>
  );
}
